import React, { useEffect, useRef, useState, useCallback } from "react";

export default function WebRTCVideoCall({ roomId = "default-room" }) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const socket = useRef(null);

  const [callStarted, setCallStarted] = useState(false);
  const [remoteOfferReceived, setRemoteOfferReceived] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);

  const playVideo = async (videoElement) => {
    try {
      await videoElement.play();
    } catch { }
  };

  const setupPeerConnection = useCallback(async (localStream) => {
    peerConnection.current = new RTCPeerConnection({
      iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
    });

    peerConnection.current.oniceconnectionstatechange = () => {
      console.log(
        "ICE connection state:",
        peerConnection.current.iceConnectionState
      );
    };

    localStream.getTracks().forEach((track) => {
      peerConnection.current.addTrack(track, localStream);
    });

    peerConnection.current.ontrack = (event) => {
      console.log("🎥 Remote track received");
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = event.streams[0];
        playVideo(remoteVideoRef.current);
      }
    };

    peerConnection.current.onicecandidate = (event) => {
      if (event.candidate) {
        socket.current.send(
          JSON.stringify({
            type: "candidate",
            candidate: event.candidate,
            room: roomId,
          })
        );
      }
    };
  }, [roomId]);

  useEffect(() => {
    let localStream;

    const init = async () => {
      if (socket.current) return;

      socket.current = new WebSocket("wss://ws.bonnardwebeditions.fr/");


      socket.current.onopen = () => {
        console.log("✅ WebSocket connected");
        socket.current.send(JSON.stringify({ type: "join", room: roomId }));
      };

      socket.current.onmessage = async (event) => {
        const data = JSON.parse(event.data);

        if (data.type === "offer") {
          console.log("📨 Offer received");
          setRemoteOfferReceived(true);

          if (!peerConnection.current) {
            await setupPeerConnection(localStream);
          }

          await peerConnection.current.setRemoteDescription(
            new RTCSessionDescription(data.offer)
          );
        }

        if (data.type === "answer") {
          console.log("📨 Answer received");
          if (peerConnection.current) {
            await peerConnection.current.setRemoteDescription(
              new RTCSessionDescription(data.answer)
            );
          }
        }

        if (data.type === "candidate") {
          console.log("📨 Candidate received");
          if (
            peerConnection.current &&
            peerConnection.current.remoteDescription
          ) {
            try {
              await peerConnection.current.addIceCandidate(
                new RTCIceCandidate(data.candidate)
              );
            } catch (e) {
              console.error("Erreur ajout ICE candidate:", e);
            }
          }
        }
      };

      socket.current.onclose = () => {
        console.log("❌ WebSocket closed");
      };

      localStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      if (localVideoRef.current) {
        if (localVideoRef.current.srcObject) {
          localVideoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        }

        localVideoRef.current.srcObject = localStream;
        await playVideo(localVideoRef.current);
      }
    };

    init();

    return () => {
      if (socket.current) {
        socket.current.close();
        socket.current = null;
      }
      if (peerConnection.current) {
        peerConnection.current.close();
        peerConnection.current = null;
      }
      if (localStream) {
        localStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [roomId, setupPeerConnection]);

  const startCall = async () => {
    if (!peerConnection.current) {
      const stream = localVideoRef.current.srcObject;
      await setupPeerConnection(stream);
    }

    const offer = await peerConnection.current.createOffer();
    await peerConnection.current.setLocalDescription(offer);

    socket.current.send(
      JSON.stringify({
        type: "offer",
        offer,
        room: roomId,
      })
    );

    setCallStarted(true);
  };

  const joinCall = async () => {
    if (!peerConnection.current) {
      const stream = localVideoRef.current.srcObject;
      await setupPeerConnection(stream);
    }

    const answer = await peerConnection.current.createAnswer();
    await peerConnection.current.setLocalDescription(answer);

    socket.current.send(
      JSON.stringify({
        type: "answer",
        answer,
        room: roomId,
      })
    );

    setCallStarted(true);
    setRemoteOfferReceived(false);
  };

  const endCall = () => {
    if (peerConnection.current) {
      peerConnection.current.close();
      peerConnection.current = null;
    }
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    setCallStarted(false);
    setRemoteOfferReceived(false);
    setIsSharingScreen(false);
  };

  const shareScreen = async () => {
    if (!peerConnection.current) return;

    try {
      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
      });

      const screenTrack = screenStream.getVideoTracks()[0];

      const sender = peerConnection.current
        .getSenders()
        .find((s) => s.track.kind === "video");

      if (sender) {
        await sender.replaceTrack(screenTrack);
        setIsSharingScreen(true);

        screenTrack.onended = async () => {
          const camTrack =
            localVideoRef.current.srcObject.getVideoTracks()[0];
          await sender.replaceTrack(camTrack);
          setIsSharingScreen(false);
        };
      }
    } catch (error) {
      console.error("Erreur partage écran:", error);
    }
  };

  return (
    <div>
      <h1>WebRTC Video Call</h1>

      <video
        ref={localVideoRef}
        autoPlay
        muted
        playsInline
        style={{
          width: "320px",
          height: "240px",
          backgroundColor: "black",
        }}
      />

      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        style={{ width: "320px", height: "240px", backgroundColor: "black" }}
      />

      <div style={{ marginTop: 10 }}>
        {!callStarted && !remoteOfferReceived && (
          <button onClick={startCall}>Démarrer l'appel</button>
        )}
        {!callStarted && remoteOfferReceived && (
          <button onClick={joinCall}>Rejoindre l'appel</button>
        )}
        {callStarted && <button onClick={endCall}>Terminer l'appel</button>}
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          onClick={shareScreen}
          disabled={!callStarted || isSharingScreen}
        >
          {isSharingScreen ? "Partage écran actif" : "Partager l'écran"}
        </button>
      </div>
    </div>
  );
}
